import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Spinner, fmtKg, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, Plus, Printer, Trash2, CheckCircle2, Package, X, Users } from "lucide-react";

const CATS = [
  { k: "cx1", label: "CX1", subtitle: "Refugo",  color: "bg-red-100 text-red-800 border-red-300", accent: "bg-red-500" },
  { k: "cx2", label: "CX2", subtitle: "Pequena", color: "bg-orange-100 text-orange-800 border-orange-300", accent: "bg-orange-500" },
  { k: "cx3", label: "CX3", subtitle: "Média",   color: "bg-emerald-100 text-emerald-800 border-emerald-300", accent: "bg-emerald-600" },
  { k: "cx3g",label: "CX3G",subtitle: "Grande",  color: "bg-sky-100 text-sky-800 border-sky-300", accent: "bg-sky-500" },
  { k: "cx4", label: "CX4", subtitle: "Extra",   color: "bg-purple-100 text-purple-800 border-purple-300", accent: "bg-purple-500" },
];

export default function BeneficiamentoProcess() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [sacariaStock, setSacariaStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Modal create pallet
  const [modal, setModal] = useState(null); // {tipo_caixa, quantidade, sacaria_tipo, sacaria_cor}
  const [showFinalize, setShowFinalize] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: invData }, { data: palletData }, sacResp] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get(`/invoices/${id}/pallets`),
        api.get(`/sacarias/stock`).catch(() => ({ data: { combinations: [] } })),
      ]);
      setInv(invData);
      setPallets(palletData || []);
      setSacariaStock(sacResp.data.combinations || []);
      if (invData.status === "beneficiada") {
        nav(`/invoices/${id}`);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const totals = CATS.map((c) => {
    const q = pallets.reduce((a, p) => {
      const it = (p.itens || []).find((x) => x.categoria === c.k);
      return a + (it ? Number(it.quantidade) || 0 : 0);
    }, 0);
    const count = pallets.filter((p) => (p.itens || []).some((x) => x.categoria === c.k)).length;
    return { ...c, quantidade: q, pallets: count };
  });
  const totalCaixasGeral = totals.reduce((a, x) => a + x.quantidade, 0);

  const openModal = (tipo) => {
    // Se for NF de terceiros e o cliente NÃO usa sacaria própria da empresa,
    // já força ignorar_sacaria=true (sacaria vem junto com a mercadoria)
    const isTerc = inv?.tipo_operacao === "terceiros";
    const forceIgnorar = isTerc && !inv?.usa_sacaria_propria;
    setModal({
      tipo_caixa: tipo,
      quantidade: 50,
      sacaria_tipo: "Genérica",
      sacaria_cor: "Amarela",
      ignorar_sacaria: forceIgnorar,
    });
  };

  const submitPallet = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/invoices/${id}/pallets`, {
        tipo_caixa: modal.tipo_caixa,
        quantidade: Number(modal.quantidade) || 0,
        sacaria_tipo: modal.sacaria_tipo,
        sacaria_cor: modal.sacaria_cor,
        ignorar_sacaria: !!modal.ignorar_sacaria,
      });
      setModal(null);
      await load();
      window.open(`/pallet-label/${data.id}`, "_blank", "noopener,noreferrer");
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const deletePallet = async (pid) => {
    if (!window.confirm("Excluir este pallet?")) return;
    try {
      await api.delete(`/pallets/${pid}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "";
      // Se falhou por estar expedido, oferece deletar como fantasma
      if (/expedid/i.test(msg)) {
        const motivo = window.prompt(
          `Este pallet aparece como EXPEDIDO no sistema.\n\n` +
          `Se ele é FANTASMA (mercadoria nunca existiu fisicamente), você pode forçar a exclusão.\n\n` +
          `Digite o motivo para auditoria (ex: "erro histórico", "fantasma reconciliado"):`,
          "fantasma"
        );
        if (!motivo) return;
        try {
          await api.delete(`/pallets/${pid}`, { params: { force: true, motivo } });
          await load();
        } catch (e2) {
          alert("Falhou ao forçar exclusão: " + formatErr(e2));
        }
      } else {
        alert(formatErr(e));
      }
    }
  };

  const finalizar = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/invoices/${id}/finalizar-beneficiamento`);
      setShowFinalize(false);
      const isTerceiros = inv?.tipo_operacao === "terceiros";
      nav(isTerceiros ? "/terceiros/nfs" : `/invoices/${id}`);
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  if (loading) return <Layout title="Carregando..."><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;
  if (!inv) return <Layout title="NF não encontrada"><Card className="p-6">Nota Fiscal não encontrada.</Card></Layout>;

  const pesoEmbalado = totalCaixasGeral * 20;
  const quebra = Math.max(0, (inv.peso_liquido || 0) - pesoEmbalado);
  const isTerc = inv.tipo_operacao === "terceiros";
  const totalSacos = totals.reduce((a, x) => a + x.quantidade, 0);
  const valorServico = isTerc ? totalSacos * Number(inv.preco_servico_sc || 0) : 0;

  return (
    <Layout
      title={isTerc
        ? `Beneficiamento a Terceiros • NF #${inv.nota_numero}`
        : `Beneficiamento • NF #${inv.nota_numero}`}
      subtitle={isTerc
        ? `Cliente: ${inv.cliente_dono} • ${inv.tipo_cebola} • ${fmtKg(inv.peso_liquido)}`
        : `${inv.fornecedor} • ${inv.tipo_cebola} • ${fmtKg(inv.peso_liquido)}`}
      actions={
        <Button variant="secondary" onClick={() => nav(isTerc ? "/terceiros/nfs" : `/invoices/${id}`)} data-testid="back-to-invoice-detail">
          <ArrowLeft size={16} /> Voltar
        </Button>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Banner específico de Terceiros */}
      {isTerc && (
        <div className="mb-5 p-4 rounded-lg bg-purple-50 border-l-4 border-purple-500 flex items-start gap-3" data-testid="terceiros-banner">
          <Users size={20} className="text-purple-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-purple-900">Beneficiamento a Terceiros</div>
            <div className="text-sm text-purple-800 mt-0.5">
              Esta mercadoria pertence a <strong>{inv.cliente_dono}</strong>. Os pallets gerados ficam isolados do estoque próprio.
            </div>
            <div className="text-xs text-purple-700 mt-1">
              Serviço: <span className="font-mono font-bold">R$ {Number(inv.preco_servico_sc || 0).toFixed(2)}/saco</span>
              {" • "}Sacaria: <strong>{inv.usa_sacaria_propria ? "própria (Serenata)" : "do cliente"}</strong>
              {totalSacos > 0 && (
                <>{" • "}Total a faturar: <span className="font-mono font-bold">R$ {valorServico.toFixed(2)}</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resumo topo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Peso líquido</div>
          <div className="font-mono text-xl font-semibold text-ink-900 mt-1">{fmtKg(inv.peso_liquido)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Total caixas</div>
          <div className="font-mono text-xl font-semibold text-brand-500 mt-1">{fmtNum(totalCaixasGeral, 0)}</div>
          <div className="text-xs text-ink-500">≈ {fmtKg(pesoEmbalado)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Pallets criados</div>
          <div className="font-mono text-xl font-semibold text-ink-900 mt-1">{pallets.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Quebra estimada</div>
          <div className="font-mono text-xl font-semibold text-danger mt-1">{fmtKg(quebra)}</div>
        </Card>
      </div>

      {/* Cards de categorias */}
      <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">Categorias de caixa</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {totals.map((c) => (
          <div key={c.k} className={`relative border rounded-xl p-4 ${c.color} shadow-sm`} data-testid={`benef-cat-${c.k}`}>
            <div className={`absolute top-0 left-0 h-1 w-full rounded-t-xl ${c.accent}`}></div>
            <div className="font-bold text-lg">{c.label}</div>
            <div className="text-xs opacity-70">{c.subtitle}</div>
            <div className="mt-3">
              <div className="text-3xl font-mono font-bold">{fmtNum(c.quantidade, 0)}</div>
              <div className="text-xs opacity-70">{c.pallets} pallet{c.pallets !== 1 ? "s" : ""}</div>
            </div>
            <button
              onClick={() => openModal(c.k)}
              className={`mt-3 w-full py-2 px-3 rounded-md ${c.accent} text-white text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5`}
              data-testid={`add-pallet-${c.k}`}
            >
              <Plus size={16} /> Novo Pallet
            </button>
          </div>
        ))}
      </div>

      {/* Lista de pallets criados */}
      <Card className="p-5 mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">
          Pallets criados ({pallets.length})
        </div>
        {pallets.length === 0 ? (
          <div className="text-sm text-ink-500 py-8 text-center">
            Nenhum pallet criado ainda. Use os botões acima para começar.
          </div>
        ) : (
          <div className="divide-y divide-ink-200">
            {pallets.map((p) => {
              const it = (p.itens || [])[0] || {};
              const cat = CATS.find((c) => c.k === it.categoria);
              return (
                <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`pallet-item-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat?.color || "bg-stone-100"}`}>
                      <Package size={18} />
                    </div>
                    <div>
                      <div className="font-mono font-semibold text-ink-900">{p.numero}</div>
                      <div className="text-xs text-ink-500">{cat?.label} • {fmtNum(it.quantidade, 0)} caixas</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/pallet-label/${p.id}`} target="_blank">
                      <button className="p-2 rounded-md hover:bg-emerald-100 text-brand-500" title="Imprimir etiqueta" data-testid={`print-pallet-${p.id}`}>
                        <Printer size={16} />
                      </button>
                    </Link>
                    <button onClick={() => deletePallet(p.id)} className="p-2 rounded-md hover:bg-red-100 text-danger" title="Excluir">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Botão Finalizar */}
      <div className="flex justify-center">
        <Button
          onClick={() => setShowFinalize(true)}
          disabled={pallets.length === 0}
          className="px-8 py-3 text-base"
          data-testid="finalize-beneficiamento-btn"
        >
          <CheckCircle2 size={20} /> Finalizar Beneficiamento
        </Button>
      </div>

      {/* Modal criar pallet */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6" data-testid="new-pallet-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Novo Pallet</div>
                <div className="text-lg font-semibold text-ink-900 mt-1">
                  Categoria {CATS.find((c) => c.k === modal.tipo_caixa)?.label} — {CATS.find((c) => c.k === modal.tipo_caixa)?.subtitle}
                </div>
              </div>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-stone-100 rounded"><X size={20} /></button>
            </div>
            <form onSubmit={submitPallet} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Quantidade de caixas</label>
                <input
                  type="number" min="1" required autoFocus
                  value={modal.quantidade}
                  onChange={(e) => setModal({ ...modal, quantidade: e.target.value })}
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-lg font-mono"
                  data-testid="pallet-quantity-input"
                />
                <div className="text-xs text-ink-500 mt-1">Padrão: 50 caixas por pallet</div>
              </div>

              {/* Toggle ignorar sacaria */}
              <label className="flex items-center gap-2 p-3 rounded-md border border-ink-200 bg-stone-50 cursor-pointer hover:bg-stone-100">
                <input
                  type="checkbox"
                  checked={!!modal.ignorar_sacaria}
                  onChange={(e) => setModal({ ...modal, ignorar_sacaria: e.target.checked })}
                  className="w-4 h-4 accent-brand-500"
                  data-testid="ignorar-sacaria-check"
                />
                <div>
                  <div className="text-sm font-semibold text-ink-900">Ignorar sacaria</div>
                  <div className="text-xs text-ink-500">Não desconta do estoque de sacaria</div>
                </div>
              </label>

              {!modal.ignorar_sacaria && (
              <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Sacaria — Tipo</label>
                  <select
                    value={modal.sacaria_tipo}
                    onChange={(e) => setModal({ ...modal, sacaria_tipo: e.target.value })}
                    className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                    data-testid="pallet-sacaria-tipo"
                  >
                    <option value="Genérica">Genérica</option>
                    <option value="Serenata">Serenata</option>
                    <option value="Usada">Usada</option>
                    <option value="Roxa">Roxa</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Sacaria — Cor</label>
                  <select
                    value={modal.sacaria_cor}
                    onChange={(e) => setModal({ ...modal, sacaria_cor: e.target.value })}
                    className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                    data-testid="pallet-sacaria-cor"
                  >
                    {["Amarela", "Branca", "Azul", "Preta", "Dourada"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {(() => {
                const combo = sacariaStock.find((s) => s.tipo === modal.sacaria_tipo && s.cor === modal.sacaria_cor);
                const disp = combo?.disponivel || 0;
                const qtd = Number(modal.quantidade) || 0;
                const insufficient = qtd > disp;
                return (
                  <div className={`p-3 rounded-md border ${insufficient ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="sacaria-stock-indicator">
                    <div className="flex items-center justify-between text-sm">
                      <span className={`font-semibold ${insufficient ? "text-danger" : "text-emerald-700"}`}>
                        Estoque: {modal.sacaria_tipo} {modal.sacaria_cor}
                      </span>
                      <span className={`font-mono font-bold ${insufficient ? "text-danger" : "text-emerald-700"}`}>
                        {disp.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} disponíveis
                      </span>
                    </div>
                    {insufficient && (
                      <div className="text-xs text-danger mt-1">
                        Faltam {(qtd - disp).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} sacos. Cadastre uma nova compra em Sacarias.
                      </div>
                    )}
                    {!insufficient && qtd > 0 && (
                      <div className="text-xs text-ink-500 mt-1">
                        Após criar: <span className="font-mono">{(disp - qtd).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}</span> restantes
                      </div>
                    )}
                  </div>
                );
              })()}
              </>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => setModal(null)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="confirm-create-pallet">
                  {busy ? <Spinner size={16} /> : <><Plus size={16} /> Criar Pallet</>}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Modal finalizar */}
      {showFinalize && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6">
            <div className="text-lg font-semibold text-ink-900 mb-2">Finalizar Beneficiamento?</div>
            <div className="text-sm text-ink-500 mb-4">
              Você criou <strong>{pallets.length} pallets</strong> totalizando <strong className="text-brand-500">{fmtNum(totalCaixasGeral, 0)} caixas</strong>. Após finalizar, você poderá definir os preços por categoria.
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
              {totals.filter((t) => t.quantidade > 0).map((c) => (
                <div key={c.k} className={`p-2 rounded ${c.color} text-center`}>
                  <div className="font-bold">{c.label}</div>
                  <div className="font-mono">{fmtNum(c.quantidade, 0)} cx</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowFinalize(false)}>Cancelar</Button>
              <Button onClick={finalizar} disabled={busy} data-testid="confirm-finalize">
                {busy ? <Spinner size={16} /> : <><CheckCircle2 size={16} /> Confirmar</>}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
